import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import debounce from "lodash/debounce";
import "./ActiveDirectoryUserSearch.scss";

const hasEmailLikeInput = (value) => String(value || "").includes("@");

const normalizeSelectedEmails = (value) => {
  const normalizeEmailList = (items = []) => {
    const seen = new Set();

    return items
      .map((item) => String(item || "").trim())
      .filter((item) => {
        if (!item) {
          return false;
        }

        const normalizedKey = item.toLowerCase();

        if (seen.has(normalizedKey)) {
          return false;
        }

        seen.add(normalizedKey);
        return true;
      });
  };

  if (Array.isArray(value)) {
    return normalizeEmailList(value);
  }

  const normalizedValue = String(value || "").trim();

  if (!normalizedValue) {
    return [];
  }

  if (normalizedValue.startsWith("[") && normalizedValue.endsWith("]")) {
    try {
      const parsedValue = JSON.parse(normalizedValue);

      if (Array.isArray(parsedValue)) {
        return normalizeEmailList(parsedValue);
      }
    } catch {
      // Fall back to comma-separated parsing below.
    }
  }

  if (normalizedValue.includes(",")) {
    return normalizeEmailList(
      normalizedValue
        .split(",")
        .map((item) => item.replace(/^[\s\[\]"]+|[\s\[\]"]+$/g, "")),
    );
  }

  return normalizeEmailList([normalizedValue]);
};

const buildSelectedUsers = (value) =>
  normalizeSelectedEmails(value).map((email) => ({
    mail: email,
    displayName: email,
  }));

const areSelectedUsersEqual = (currentUsers = [], nextUsers = []) => {
  if (currentUsers.length !== nextUsers.length) {
    return false;
  }

  return currentUsers.every((user, index) => {
    const nextUser = nextUsers[index];

    return (
      user?.mail === nextUser?.mail &&
      user?.displayName === nextUser?.displayName
    );
  });
};

// Graph API endpoint for user search
const GRAPH_USERS_SEARCH_URL = "https://graph.microsoft.com/v1.0/users";
const SUGGESTION_LIST_MAX_HEIGHT = 200;
const SUGGESTION_ITEM_ESTIMATED_HEIGHT = 48;
const SUGGESTION_LIST_GAP = 4;
const CLIPPING_OVERFLOW_VALUES = new Set(["auto", "scroll", "hidden", "clip"]);
const SCROLLABLE_OVERFLOW_VALUES = new Set(["auto", "scroll"]);

/**
 * Securely parses JSON response with validation.
 * Addresses Checkmarx HSTS findings by using text-based parsing with validation.
 * @param {Response} response - Fetch API response object
 * @returns {Promise<object>} - Parsed JSON data
 */
const secureJsonParse = async (response) => {
  const text = await response.text();
  if (!text || text.trim().length === 0) {
    return {};
  }
  return JSON.parse(text);
};

const hasOverflowValue = (value, allowedValues) => {
  return String(value || "")
    .split(" ")
    .some((item) => allowedValues.has(item));
};

const getViewportHeight = () => {
  return window.innerHeight || document.documentElement.clientHeight || 0;
};

const getPlacementContext = (element) => {
  const viewportHeight = getViewportHeight();
  const context = {
    boundaryTop: 0,
    boundaryBottom: viewportHeight,
    scrollParents: [],
  };

  let current = element?.parentElement;

  while (
    current &&
    current !== document.body &&
    current !== document.documentElement
  ) {
    const computedStyle = window.getComputedStyle(current);
    const { overflow, overflowY } = computedStyle;
    const clipsVertically =
      hasOverflowValue(overflowY, CLIPPING_OVERFLOW_VALUES) ||
      hasOverflowValue(overflow, CLIPPING_OVERFLOW_VALUES);

    if (clipsVertically) {
      const rect = current.getBoundingClientRect();
      context.boundaryTop = Math.max(context.boundaryTop, rect.top);
      context.boundaryBottom = Math.min(context.boundaryBottom, rect.bottom);
    }

    const scrollsVertically =
      hasOverflowValue(overflowY, SCROLLABLE_OVERFLOW_VALUES) ||
      hasOverflowValue(overflow, SCROLLABLE_OVERFLOW_VALUES);

    if (scrollsVertically) {
      context.scrollParents.push(current);
    }

    current = current.parentElement;
  }

  if (context.boundaryBottom <= context.boundaryTop) {
    context.boundaryTop = 0;
    context.boundaryBottom = viewportHeight;
  }

  return context;
};

// Fetches user suggestions from Microsoft Graph Users API
const fetchUserSuggestions = async (searchText, accessToken) => {
  if (!searchText || searchText.length < 2 || !accessToken) {
    return [];
  }

  try {
    const filter = `startswith(displayName,'${searchText}') or startswith(mail,'${searchText}') or startswith(userPrincipalName,'${searchText}')`;
    const response = await fetch(
      `${GRAPH_USERS_SEARCH_URL}?$filter=${encodeURIComponent(filter)}&$top=10&$select=id,displayName,mail`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      console.error("Graph API error:", response.status);
      return [];
    }

    const data = await secureJsonParse(response);
    return (data.value || [])
      .filter((user) => user.mail && user.mail.toLowerCase().endsWith("gds.ey.com"))
      .map((user) => ({
        id: user.id,
        displayName: user.displayName,
        mail: user.mail || "",
      }));
  } catch (error) {
    console.error("Error fetching user suggestions:", error);
    return [];
  }
};

// Active Directory User Search Component
const ActiveDirectoryUserSearch = ({
  value,
  onChange,
  onPendingSelectionChange,
  disabled,
  placeholder = "Search for user...",
  hasError,
  fieldKey,
  allowMultiple = false,
}) => {
  const { instance, accounts, inProgress } = useMsal();
  const [accessToken, setAccessToken] = useState("");
  const [searchText, setSearchText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(() => {
    const initialSelectedUsers = buildSelectedUsers(value);

    return allowMultiple
      ? initialSelectedUsers
      : initialSelectedUsers.slice(0, 1);
  });
  const [suggestionPlacement, setSuggestionPlacement] = useState("down");
  const suggestionRef = useRef(null);
  const suggestionsListRef = useRef(null);
  const multiControlRef = useRef(null);
  const searchInputRef = useRef(null);
  const selectedUser = allowMultiple ? null : selectedUsers[0] || null;

  // Acquire access token for Graph API
  useEffect(() => {
    if (
      inProgress === InteractionStatus.None &&
      accounts.length > 0 &&
      !accessToken
    ) {
      const request = {
        scopes: ["User.Read", "User.ReadBasic.All"],
        account: accounts[0],
      };

      instance
        .acquireTokenSilent(request)
        .then((response) => {
          setAccessToken(response.accessToken);
        })
        .catch((error) => {
          console.error("Silent token acquisition failed:", error);
        });
    }
  }, [inProgress, accounts, instance, accessToken]);

  useEffect(() => {
    const nextSelectedUsers = buildSelectedUsers(value);
    const normalizedSelectedUsers = allowMultiple
      ? nextSelectedUsers
      : nextSelectedUsers.slice(0, 1);

    setSelectedUsers((previousSelectedUsers) =>
      areSelectedUsersEqual(previousSelectedUsers, normalizedSelectedUsers)
        ? previousSelectedUsers
        : normalizedSelectedUsers,
    );
  }, [allowMultiple, value]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionRef.current &&
        !suggestionRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateSuggestionPlacement = useCallback(() => {
    if (!suggestionRef.current) {
      return;
    }

    const containerRect = suggestionRef.current.getBoundingClientRect();
    const { boundaryTop, boundaryBottom } = getPlacementContext(
      suggestionRef.current,
    );
    const measuredHeight =
      suggestionsListRef.current?.getBoundingClientRect().height || 0;
    const estimatedHeight = Math.min(
      suggestions.length * SUGGESTION_ITEM_ESTIMATED_HEIGHT,
      SUGGESTION_LIST_MAX_HEIGHT,
    );
    const dropdownHeight =
      measuredHeight > 0 ? measuredHeight : estimatedHeight;
    const spaceAbove = Math.max(containerRect.top - boundaryTop, 0);
    const spaceBelow = Math.max(boundaryBottom - containerRect.bottom, 0);
    const requiredSpace = dropdownHeight + SUGGESTION_LIST_GAP;

    setSuggestionPlacement(
      spaceBelow >= requiredSpace || spaceBelow >= spaceAbove ? "down" : "up",
    );
  }, [suggestions.length]);

  useLayoutEffect(() => {
    if (!showSuggestions || suggestions.length === 0) {
      setSuggestionPlacement("down");
      return undefined;
    }

    const { scrollParents } = getPlacementContext(suggestionRef.current);
    const animationFrameId = window.requestAnimationFrame(
      updateSuggestionPlacement,
    );

    updateSuggestionPlacement();
    window.addEventListener("resize", updateSuggestionPlacement);
    window.addEventListener("scroll", updateSuggestionPlacement, true);
    scrollParents.forEach((parent) => {
      parent.addEventListener("scroll", updateSuggestionPlacement);
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", updateSuggestionPlacement);
      window.removeEventListener("scroll", updateSuggestionPlacement, true);
      scrollParents.forEach((parent) => {
        parent.removeEventListener("scroll", updateSuggestionPlacement);
      });
    };
  }, [showSuggestions, suggestions.length, updateSuggestionPlacement]);

  // Debounced search function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (text) => {
      if (text.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoading(true);
      const results = await fetchUserSuggestions(text, accessToken);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
      setIsLoading(false);
    }, 300),
    [accessToken],
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const text = e.target.value;
    setSearchText(text);

    if (!allowMultiple) {
      setSelectedUsers([]);
    }

    onPendingSelectionChange?.(String(text || "").trim() !== "");

    // Pass email-like input through so parent field validation can flag invalid
    // contributor email values while the user is typing.
    if (!allowMultiple) {
      onChange(hasEmailLikeInput(text) ? text : "");
    }

    debouncedSearch(text);
  };

  // Handle user selection
  const handleSelectUser = (user) => {
    if (allowMultiple) {
      const nextSelectedUsers = buildSelectedUsers([
        ...selectedUsers.map((selectedItem) => selectedItem.mail),
        user.mail,
      ]);

      setSelectedUsers(nextSelectedUsers);
      onChange(nextSelectedUsers.map((selectedItem) => selectedItem.mail));
    } else {
      setSelectedUsers([user]);
      onChange(user.mail); // Only pass the email
    }

    onPendingSelectionChange?.(false);
    //onChange(user.mail); // Only pass the email
    setSearchText("");
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Clear selected user
  const handleClear = () => {
    setSelectedUsers([]);
    setSearchText("");
    onPendingSelectionChange?.(false);
    onChange(allowMultiple ? [] : "");
  };

  const handleRemoveUser = (emailToRemove) => {
    const nextSelectedUsers = selectedUsers.filter(
      (user) => user.mail !== emailToRemove,
    );

    setSelectedUsers(nextSelectedUsers);
    onPendingSelectionChange?.(false);
    onChange(nextSelectedUsers.map((user) => user.mail));
  };

  const focusSearchInput = () => {
    if (disabled) {
      return;
    }

    searchInputRef.current?.focus();

    if (allowMultiple && multiControlRef.current) {
      multiControlRef.current.scrollTop = multiControlRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (!allowMultiple || !multiControlRef.current) {
      return;
    }

    multiControlRef.current.scrollTop = multiControlRef.current.scrollHeight;
  }, [allowMultiple, searchText, selectedUsers.length]);

  // Get display value
  const getDisplayValue = () => {
    if (selectedUser) {
      return selectedUser.displayName || selectedUser.mail;
    }
    return searchText;
  };

  const inputTooltipTitle = String(getDisplayValue() || "").trim() || undefined;
  const selectedUserTooltipTitle =
    String(selectedUser?.mail || selectedUser?.displayName || "").trim() ||
    undefined;
  const multiInputPlaceholder =
    allowMultiple && selectedUsers.length > 0 ? "" : placeholder;
  const searchClassName = [
    "ad-user-search",
    showSuggestions ? "is-open" : "",
    showSuggestions ? `open-${suggestionPlacement}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={searchClassName} ref={suggestionRef}>
      <div
        className={`ad-user-search-input-wrapper ${hasError ? "has-error" : ""} ${allowMultiple ? "is-multi" : ""}`}
      >
        {allowMultiple ? (
          <div
            ref={multiControlRef}
            className={`ad-user-search-multi-control ${hasError ? "field-error" : ""}`}
            onClick={focusSearchInput}
          >
            {selectedUsers.map((user) => {
              const selectedValueTitle =
                String(user?.mail || user?.displayName || "").trim() ||
                undefined;

              return (
                <div
                  key={user.mail}
                  className="ad-user-selected ad-user-selected-chip"
                  title={selectedValueTitle}
                >
                  <span className="ad-user-selected-email">{user.mail}</span>
                  {!disabled && (
                    <button
                      type="button"
                      className="ad-user-clear-btn"
                      onClick={() => handleRemoveUser(user.mail)}
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
            <input
              ref={searchInputRef}
              type="text"
              className={`ad-user-search-input ad-user-search-input-inline ${hasError ? "field-error" : ""}`}
              value={searchText}
              title={inputTooltipTitle}
              onChange={handleSearchChange}
              disabled={disabled}
              placeholder={multiInputPlaceholder}
              autoComplete="off"
            />
            {isLoading && <span className="ad-user-loading">...</span>}
          </div>
        ) : selectedUser ? (
          <div className="ad-user-selected" title={selectedUserTooltipTitle}>
            <span className="ad-user-selected-email">{selectedUser.mail}</span>
            {!disabled && (
              <button
                type="button"
                className="ad-user-clear-btn"
                onClick={handleClear}
              >
                ×
              </button>
            )}
          </div>
        ) : (
          <input
            type="text"
            className={`ad-user-search-input ${hasError ? "field-error" : ""}`}
            value={searchText}
            title={inputTooltipTitle}
            onChange={handleSearchChange}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
          />
        )}

        {!allowMultiple && isLoading && (
          <span className="ad-user-loading">...</span>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="ad-user-suggestions" ref={suggestionsListRef}>
          {suggestions.map((user) => (
            <li
              key={user.id}
              className="ad-user-suggestion-item"
              onClick={() => handleSelectUser(user)}
            >
              <span className="ad-user-suggestion-name">
                {user.displayName}
              </span>
              <span className="ad-user-suggestion-email">{user.mail}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ActiveDirectoryUserSearch;
